/* prettier-ignore */

import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { RestService } from '../../services/rest.service';
import { StatisticsService } from '../../services/statistics.service';
import { LoginResponse, LoginResponseError } from '../interfaces';
import { TranslationService } from 'src/app/services/translation.service';
import { ModalController } from '@ionic/angular';
import { LocalStorageService, Snackbar } from 'src/app/services';
import { UserUpdatedResponse } from 'src/app/user/interfaces';

@Injectable({ providedIn: 'root' })
export class AuthService {
  // Injected services
  private _translationService = inject(TranslationService);
  private _router = inject(Router);
  private _http = inject(HttpClient);
  private _restService = inject(RestService);
  private _statisticsService = inject(StatisticsService);
  private _snackbarService = inject(Snackbar);
  private _localStorageService = inject(LocalStorageService); // TODO: DELETE FOR SSR -> NEW LOGIN STRATEGY (SECURE COOKIE)
  private _modalCtrl = inject(ModalController); // TODO: DELETE FOR SSR -> DIALOG MATERIAL ANGULAR/DS
  
  // Signals state
  private _user = signal<UserUpdatedResponse | null>(null);
  public user = this._user.asReadonly();
  
  private _isUserLoggedOut = signal<boolean>(false);
  public isUserLoggedOut = this._isUserLoggedOut.asReadonly();

  /**
   * HRS-3134: el último intento de acceso ha fallado.
   *
   * Lo miran las dos pantallas de login para ofrecer el enlace de acceso a quien se
   * registró en una oficina de turismo: su cuenta se creó sin contraseña, así que lo
   * único que recibe es un «credenciales incorrectas» que no le dice nada. No se
   * distingue esa cuenta de una contraseña mal escrita a propósito: decir «esta
   * cuenta no tiene contraseña» confirmaría que ese correo está registrado.
   */
  private _loginFailed = signal<boolean>(false);
  public loginFailed = this._loginFailed.asReadonly();
  
  // Setters
  public setUser(user: UserUpdatedResponse | null) {
    this._user.set(user);
  }
  
  public setIsUserLoggedOut(isLoggedOut: boolean) {
    this._isUserLoggedOut.set(isLoggedOut);
  }
  
  // Computed signals
  public isUserLogged = computed(() => !!this._user());
  
  // Methods
  public updateUserByNameAndBirthDate(name: string, date_of_birth: string): void {
    this._user.update((currentUser) => {
      if (currentUser) {
        return { 
          ...currentUser, 
          name: name, 
          date_of_birth: date_of_birth 
        };
      }
      return currentUser;
    });
  }
  
  public loginServiceSignal(email: string, password: string): void {
    this._login(email, password)
      .subscribe({
        next: (response: LoginResponse) => {
          this._user.set(response.data.user);
          this._localStorageService.setItem('loginResponse', response, 2 * 3600); // TODO: DELETE FOR SSR -> NEW LOGIN STRATEGY (SECURE COOKIE)
          this._localStorageService.setItem('user', response.data.user, 2 * 3600); // TODO: REMOVE LATER
          this._statisticsService.login = response.data.user.email; // TODO: REVIEW FOR SSR
          
          const successMessage = this._translationService.instant('Inicio de sesión exitoso');
          this._snackbarService.showSuccessSnackbar(successMessage);
          this.dismissModal();
        },
        error: (error: LoginResponseError) => {
          this._snackbarService.showErrorSnackbar(error.message);
        }
      });
  }
  
  private _login(email: string, password: string): Observable<LoginResponse> {
    const payload = { email, password }
    this._loginFailed.set(false);

    return this._http.post(`${this._restService.apiUrlBase}/api/webapp-login`, payload)
      .pipe(
        catchError((error) => {
          // HRS-3134: aquí, y no en quien llama, porque las dos pantallas de login
          // pasan por este método y cada una gestiona su error por su cuenta.
          this._loginFailed.set(true);

          return throwError(() => error.error);
        }),
        map((response: LoginResponse) => {
          this._isUserLoggedOut.set(false);
          return response;
        }
    ));
  }

  // Social login (HRS-2514, flujo redirect): el back devuelve al usuario a la webapp
  // con ?social_login_code=<código de un solo uso> (o ?social_login_error=...). Se llama
  // una vez al arrancar la app: canjea el código por el token Sanctum y limpia la URL.
  public handleSocialLoginReturn(): void {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('social_login_code');
    const error = params.get('social_login_error');

    if (!code && !error) return;

    params.delete('social_login_code');
    params.delete('social_login_error');
    const query = params.toString();
    const cleanUrl = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
    history.replaceState(null, '', cleanUrl);

    if (error) {
      // 'cancelled' = el usuario cerró la pantalla del proveedor: no es un error.
      if (error !== 'cancelled') {
        // 'not_registered' = intentó iniciar sesión social sin cuenta previa
        // (el alta solo existe en el flujo de registro).
        const message = this._translationService.instant(
          error === 'not_registered'
            ? 'No existe ninguna cuenta con ese email. Regístrate primero'
            : 'No se ha podido completar el inicio de sesión',
        );
        this._snackbarService.showErrorSnackbar(message, undefined, document.body);
      }
      return;
    }

    this._exchangeSocialLoginCode(code).subscribe({
      next: () => {
        const successMessage = this._translationService.instant('Inicio de sesión exitoso');
        this._snackbarService.showSuccessSnackbar(successMessage, undefined, document.body);
      },
      error: () => {
        const message = this._translationService.instant('No se ha podido completar el inicio de sesión');
        this._snackbarService.showErrorSnackbar(message, undefined, document.body);
      },
    });
  }

  private _exchangeSocialLoginCode(code: string): Observable<LoginResponse> {
    return this._http.post(`${this._restService.apiUrlBase}/api/webapp-social-exchange`, { code })
      .pipe(
        catchError((error) => {
          return throwError(() => error.error);
        }),
        map((response: LoginResponse) => {
          this._isUserLoggedOut.set(false);
          this._user.set(response.data.user);
          this._localStorageService.setItem('loginResponse', response, 2 * 3600); // TODO: DELETE FOR SSR -> NEW LOGIN STRATEGY (SECURE COOKIE)
          this._localStorageService.setItem('user', response.data.user, 2 * 3600); // TODO: REMOVE LATER
          this._statisticsService.login = response.data.user.email;
          return response;
        }),
      );
  }

  public logout(isTechMode: boolean = false) {
    this._statisticsService.login = '';
    this._clearStoredSession();
    if (!isTechMode) {
      this._isUserLoggedOut.set(true);
    }
    this._user.set(null);
    this._router.navigateByUrl('/home');
  }

  public customLogOut(){
      this._clearStoredSession();
      this._user.set(null);
  }

  /**
   * HRS-3482: borra TAMBIÉN el token (loginResponse). El interceptor lo lee de
   * localStorage y, si se quedaba, una compra "como invitado" tras cerrar sesión
   * salía con el Bearer del usuario anterior y se le asignaba a él.
   */
  private _clearStoredSession(): void {
    this._localStorageService.removeItem('user'); // TODO: DELETE FOR SSR -> NEW LOGIN STRATEGY (SECURE COOKIE)
    this._localStorageService.removeItem('loginResponse');
  }

  public register(email: string, password: string) {
    // name_index: origen del registro (HRS-2825); el back lo valida contra BD
    const payload: Record<string, string> = { email, password }
    if (this._restService.subDomain) payload['name_index'] = this._restService.subDomain;
    return this._http.post(`${this._restService.apiUrlBase}/api/send-register`, payload)
  }
  
  public loginAndSetUser(email: string, password: string): Observable<LoginResponse> {
    return this._login(email, password).pipe(
      map((response: LoginResponse) => {
        this._user.set(response.data.user);
        this._localStorageService.setItem('loginResponse', response, 2 * 3600);
        this._localStorageService.setItem('user', response.data.user, 2 * 3600);
        this._statisticsService.login = response.data.user.email;
        return response;
      }),
    );
  }

  /**
   * HRS-3134: le manda por correo el enlace para entrar.
   *
   * Es el mismo de recuperar contraseña: a una cuenta sin contraseña —la que se le
   * crea al registrarse en la oficina— le sirve igual, y así no hay un segundo flujo
   * que mantener. El back responde lo mismo exista o no el correo, así que no hay
   * nada que mirar en la respuesta.
   */
  public sendAccessLink(email: string): Observable<unknown> {
    return this._http.post(`${this._restService.apiUrlBase}/api/password/reset`, { email });
  }

  dismissModal(){
    this._modalCtrl.dismiss().catch(e => {
      console.warn("No modal found on dismiss")
    });
  }
}